/**
 * Authentication Utilities
 *
 * Enhanced auth utilities with JWT token management and API integration
 * Maintains backward compatibility while adding new features
 */

import { authService } from '../services/api/authService';
import type { AuthUser, TokenData } from '../services/api/types';

// Legacy interface for backward compatibility
interface UserData {
  name: string;
  email: string;
  role: string;
}

/**
 * Check if user is authenticated
 * Uses the new auth service with fallback to legacy token check
 */
export const isAuthenticated = (): boolean => {
  // First check with new auth service
  if (authService.isAuthenticated()) {
    return true;
  }

  // Fallback to legacy token check for backward compatibility
  const legacyToken = localStorage.getItem('authToken');
  return !!legacyToken;
};

/**
 * Get current user data
 * Uses new auth service with fallback to legacy storage
 */
export const getCurrentUser = async (): Promise<UserData | null> => {
  try {
    // Try to get user from new auth service
    const authUser = await authService.getCurrentUser();
    if (authUser) {
      return {
        name: authUser.name || authUser.email,
        email: authUser.email,
        role: 'user', // Default role, can be enhanced later
      };
    }
  } catch (error) {
    console.warn('Failed to get user from auth service:', error);
  }

  // Fallback to legacy storage
  const userData = localStorage.getItem('userData');
  if (userData) {
    try {
      return JSON.parse(userData);
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Get current user synchronously (legacy compatibility)
 * @deprecated Use getCurrentUser() instead for better error handling
 */
export const getCurrentUserSync = (): UserData | null => {
  const userData = localStorage.getItem('userData');
  if (userData) {
    try {
      return JSON.parse(userData);
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Set user data (legacy compatibility)
 * @deprecated User data is now managed automatically by auth service
 */
export const setUserData = (user: UserData): void => {
  localStorage.setItem('userData', JSON.stringify(user));
};

/**
 * Enhanced logout with API call
 * Logs out from current session and clears local data
 */
export const logout = async (): Promise<void> => {
  try {
    const tokens = authService.getStoredTokens();
    if (tokens?.session_id) {
      // Logout from current session via API
      await authService.logout(tokens.session_id);
    }
  } catch (error) {
    console.warn('Failed to logout via API:', error);
  } finally {
    // Always clear local data (including establishment)
    authService.clearLocalAuth();
    // Clear establishment selection explicitly
    authService.clearEstablishmentSelection();
    // Clear legacy tokens and data
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('user_info');
    localStorage.removeItem('current_establishment');
  }
};

/**
 * Logout from all sessions
 * Logs out from all active sessions and clears local data
 */
export const logoutAll = async (): Promise<void> => {
  try {
    await authService.logoutAll();
  } catch (error) {
    console.warn('Failed to logout from all sessions:', error);
  } finally {
    // Always clear local data (including establishment)
    authService.clearLocalAuth();
    // Clear establishment selection explicitly
    authService.clearEstablishmentSelection();
    // Clear legacy tokens and data
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('user_info');
    localStorage.removeItem('current_establishment');
  }
};

/**
 * Legacy synchronous logout
 * @deprecated Use logout() instead for proper API cleanup
 */
export const logoutSync = (): void => {
  authService.clearLocalAuth();
  // Clear establishment selection
  authService.clearEstablishmentSelection();
  // Clear legacy tokens and data
  localStorage.removeItem('authToken');
  localStorage.removeItem('userData');
  localStorage.removeItem('user_info');
  localStorage.removeItem('current_establishment');
};

/**
 * Get user initials from name
 */
export const getUserInitials = (name: string): string => {
  return name
    ?.split(' ')
    ?.map((part) => part.charAt(0).toUpperCase())
    ?.slice(0, 2)
    ?.join('');
};

/**
 * Check if access token is expired
 */
export const isTokenExpired = (): boolean => {
  const tokens = authService.getStoredTokens();
  if (!tokens?.expires_at) {
    return true;
  }
  return Date.now() > tokens.expires_at;
};

/**
 * Check if refresh token is available
 */
export const hasRefreshToken = (): boolean => {
  const tokens = authService.getStoredTokens();
  return !!tokens?.refresh_token;
};

/**
 * Get stored JWT tokens
 */
export const getTokens = (): TokenData | null => {
  return authService.getStoredTokens();
};

/**
 * Get access token for API requests
 */
export const getAccessToken = (): string | null => {
  const tokens = authService.getStoredTokens();
  return tokens?.access_token || null;
};

/**
 * Get current session ID
 */
export const getSessionId = (): string | null => {
  const tokens = authService.getStoredTokens();
  return tokens?.session_id || null;
};

/**
 * Get current user ID
 */
export const getUserId = (): string | null => {
  const tokens = authService.getStoredTokens();
  return tokens?.user_id || null;
};

/**
 * Decode JWT token payload (basic implementation)
 * Note: This doesn't verify the token signature, just decodes the payload
 */
export const decodeToken = (token: string): any | null => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;

    const decoded = atob(payload);
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Check if user needs profile selection
 * Based on login response needs_profile_selection flag
 */
export const needsProfileSelection = (): boolean => {
  // This would be stored after login if needed
  const profileSelection = localStorage.getItem('needs_profile_selection');
  return profileSelection === 'true';
};

/**
 * Set profile selection requirement
 */
export const setNeedsProfileSelection = (needs: boolean): void => {
  localStorage.setItem('needs_profile_selection', needs.toString());
};
